import { sdkEvents } from '../core/events.js';
import { addD6String } from './d6.js';

const BPS = 10_000n;
const AMM_TRADE_KIND = '1';
const SEQUENCE_LENGTH = 15;

interface WinnerPoolState {
  readonly quoteVaultReserveD6: string;
  readonly outcomeVaultReserveD6: string;
  readonly observedAtUnixSeconds: number;
}

interface WinnerLike {
  readonly mint?: string;
  readonly outcomeIndex?: number;
  readonly price?: string;
  readonly change1hBps?: string | null;
  readonly change24hBps?: string | null;
  readonly volumeTotal?: string;
  readonly cpQuoteReserveD6?: string;
  readonly indexedOutcomeMintSupplyD6?: string;
  readonly effectiveMarketCapD6?: string;
  readonly currentPoolState?: WinnerPoolState;
  readonly parentMarket?: { readonly pubkey?: string };
}

interface ValidAmmTrade {
  readonly timestampUnixSeconds: number;
  readonly amountD6: string;
  readonly priceD9: string;
  readonly quoteReserveD6: string;
  readonly outcomeReserveD6: string;
}

export function applyAmmActivityToWinner<T extends WinnerLike>(
  prev: T,
  raw: Record<string, unknown>,
  _args: Record<string, unknown>,
  subjectMint?: string,
): T {
  if (subjectMint === undefined || prev.mint !== subjectMint) return prev;
  const pool = prev.currentPoolState;
  if (pool === undefined) return prev;

  const activity = validateTrade(prev, raw);
  if (activity === null) return prev;

  const previousPrice = positiveInteger(prev.price);
  const nextPrice = BigInt(activity.priceD9);
  const marketCap = marketCapAtPrice(prev.indexedOutcomeMintSupplyD6, nextPrice);
  const nextPool = {
    ...pool,
    quoteVaultReserveD6: activity.quoteReserveD6,
    outcomeVaultReserveD6: activity.outcomeReserveD6,
    observedAtUnixSeconds: Math.max(pool.observedAtUnixSeconds, activity.timestampUnixSeconds),
  };
  return {
    ...prev,
    price: activity.priceD9,
    volumeTotal: addD6String(prev.volumeTotal, activity.amountD6),
    cpQuoteReserveD6: activity.quoteReserveD6,
    ...(marketCap === null ? {} : { effectiveMarketCapD6: marketCap }),
    ...(previousPrice === null
      ? {}
      : {
          change1hBps: repriceChangeBps(prev.change1hBps, previousPrice, nextPrice),
          change24hBps: repriceChangeBps(prev.change24hBps, previousPrice, nextPrice),
        }),
    currentPoolState: nextPool,
  } as T;
}

function validateTrade(prev: WinnerLike, raw: Record<string, unknown>): ValidAmmTrade | null {
  if (!(raw.sequenceId instanceof Uint8Array) || raw.sequenceId.byteLength !== SEQUENCE_LENGTH) {
    rejectMalformed(prev, raw, 'sequence');
    return null;
  }
  const kind = readInteger(raw, 'kind');
  const timestamp = readInteger(raw, 'ts');
  const amountD6 = readPositiveInteger(raw, 'amountD6');
  const priceD9 = readPositiveInteger(raw, 'postOutcomePriceD9');
  const quoteReserveD6 = readPositiveInteger(raw, 'postQuoteReservesD6');
  const outcomeReserveD6 = readPositiveInteger(raw, 'postOutcomeReservesD6');
  const market = readString(raw, 'market');
  const outcomeIndex = readInteger(raw, 'outcomeIdx');
  if (
    kind !== AMM_TRADE_KIND ||
    timestamp === null ||
    amountD6 === null ||
    priceD9 === null ||
    quoteReserveD6 === null ||
    outcomeReserveD6 === null ||
    market === null ||
    outcomeIndex === null
  ) {
    rejectMalformed(prev, raw, 'payload');
    return null;
  }
  if (market !== prev.parentMarket?.pubkey || Number(outcomeIndex) !== prev.outcomeIndex)
    return null;

  const timestampUnixSeconds = Number(timestamp);
  if (!Number.isSafeInteger(timestampUnixSeconds)) return null;
  if (BigInt(priceD9) !== (BigInt(quoteReserveD6) * 1_000_000_000n) / BigInt(outcomeReserveD6)) {
    rejectMalformed(prev, raw, 'spot_price');
    return null;
  }
  return { timestampUnixSeconds, amountD6, priceD9, quoteReserveD6, outcomeReserveD6 };
}

function readField(raw: Record<string, unknown>, key: string): unknown {
  const payload = raw.payload;
  if (payload !== null && typeof payload === 'object' && key in payload) {
    return (payload as Record<string, unknown>)[key];
  }
  return raw[key];
}

function readInteger(raw: Record<string, unknown>, key: string): string | null {
  const value = readField(raw, key);
  if (typeof value === 'bigint') return value >= 0n ? value.toString() : null;
  if (typeof value === 'number') {
    return Number.isSafeInteger(value) && value >= 0 ? String(value) : null;
  }
  if (typeof value !== 'string' || !/^(?:0|[1-9]\d*)$/.test(value)) return null;
  return value;
}

function readPositiveInteger(raw: Record<string, unknown>, key: string): string | null {
  const value = readInteger(raw, key);
  return value !== null && BigInt(value) > 0n ? value : null;
}

function positiveInteger(value: string | undefined): bigint | null {
  return value !== undefined && /^(?:0|[1-9]\d*)$/.test(value) && BigInt(value) > 0n
    ? BigInt(value)
    : null;
}

function readString(raw: Record<string, unknown>, key: string): string | null {
  const value = readField(raw, key);
  return typeof value === 'string' && value.length > 0 ? value : null;
}

function marketCapAtPrice(supplyD6: string | undefined, priceD9: bigint): string | null {
  const supply = positiveInteger(supplyD6);
  return supply === null ? null : ((supply * priceD9 + 500_000_000n) / 1_000_000_000n).toString();
}

function repriceChangeBps(
  previousBps: string | null | undefined,
  previousPrice: bigint,
  nextPrice: bigint,
): string | null {
  if (previousBps === null || previousBps === undefined) return null;
  return ((nextPrice * (BPS + BigInt(previousBps))) / previousPrice - BPS).toString();
}

function rejectMalformed(prev: WinnerLike, raw: Record<string, unknown>, reason: string): void {
  sdkEvents.emit('applyEvent:error', {
    specName: 'useGetWinner',
    subject: typeof raw.subject === 'string' ? raw.subject : undefined,
    queryKey: ['outcomes', prev.mint],
    error: 'winner.amm_event_rejected',
    reason,
  });
}
